/** @format */

import React, { FC, useState, useEffect } from 'react'
import styled from 'styled-components'
import {
  Button,
  Select,
  Option,
  Icon,
  Table,
  TableColumn,
  Switch,
  TableRowKey,
  Pagination,
  Dialog,
  Message,
  Ellipsis
} from '@king-design/react'
import { useHistory } from 'react-router-dom'
import {
  MonitorSearch,
  DeleteConfirm,
  TablePage,
  TableSearch,
  getURLTableSearchParams
} from 'src/components'
import { PrometheusAlarmTabProps } from 'src/pages/PrometheusMonitor/Alarm'
import { PageProps, initPage } from 'src/utils/interface'
import { useAsyncFn } from 'react-use'
import {
  getPrometheusAlertPolicyList,
  deletePrometheusAlertPolicy,
  deletePrometheusAlertRule,
  updatePrometheusAlertPolicyRuleState,
  updatePrometheusAlertRuleState
} from 'src/api/ApiPrometheusAlarm'
import { handleWayToChannels, reciverScheme } from 'src/components/AlarmReciver/new'
import { urlNoticeScheme, handleCallbackType } from 'src/components/AlarmCallBack/new'

interface SearchProps extends PageProps {
  Instance: string
  State: 0 | 1 | 2
  PolicyName: string
}

const initSearch: SearchProps = {
  Instance: '',
  State: 0,
  PolicyName: '',
  ...initPage
}

const PrometheusAlarmPolicy: FC<PrometheusAlarmTabProps> = ({ className, instances, region }) => {
  const history = useHistory()
  const [search, setSearch] = useState<SearchProps>(getURLTableSearchParams() || initSearch)
  const [spreadKeys, setSpreadKeys] = useState<TableRowKey[]>([])

  const [{ value }, fetch] = useAsyncFn(
    async (values?) => {
      const params = {
        Region: region,
        SearchPolicyName: search.PolicyName,
        ...search,
        ...values
      }
      const result = await getPrometheusAlertPolicyList(params)
      const policys = result?.Polices.map(item => {
        if (item.Rules?.[0]) {
          const { Expr, For, RuleID, State, Alert } = item.Rules?.[0]
          const isRule = item.Rules?.length === 1
          return {
            ...item,
            children:
              item.Rules?.length > 1
                ? item.Rules.filter(rule => {
                    if (params.State === 0) {
                      return true
                    } else {
                      return rule.State === params.State
                    }
                  })
                : undefined,
            rules: item.Rules,
            isRule: !!isRule,
            RuleID: isRule ? RuleID : undefined,
            Alert: isRule ? Alert : undefined,
            State: isRule ? State : undefined,
            Expr: isRule ? Expr : undefined,
            For: isRule ? For : undefined
          }
        } else {
          return item
        }
      })
      return {
        data: policys,
        total: result?.TotalCount
      }
    },
    [search, region]
  )

  // 删除策略/删除规则
  const [, deleteFetch] = useAsyncFn(
    async params => {
      const result = params.PolicyID
        ? await deletePrometheusAlertPolicy({
            PolicyID: params.PolicyID
          })
        : await deletePrometheusAlertRule({
            RuleID: params.RuleID
          })
      if (result?.code === 'Success') {
        Message.success('删除成功')
        setSearch({
          ...search,
          Page: 1
        })
      } else {
        result?.message && Message.error(result?.message)
      }
    },
    [search]
  )

  // 修改规则启停状态
  const [, updateStateFetch] = useAsyncFn(
    async params => {
      const result = params.RuleID
        ? await updatePrometheusAlertRuleState({
            RuleID: params.RuleID,
            State: params.State ? 1 : 2
          })
        : await updatePrometheusAlertPolicyRuleState({
            PolicyID: params.PolicyID,
            State: params.State ? 1 : 2
          })
      if (result?.code === 'error') {
        result?.message && Message.error(result?.message)
      } else {
        Message.success('规则状态修改成功')
        fetch()
      }
    },
    [search, region]
  )

  useEffect(() => {
    fetch()
  }, [search, region])

  return (
    <div className={className}>
      <div className="header">
        <Button
          type="primary"
          onClick={() => {
            history.push('/monitor/prometheusmonitor/alarm/edit?tab=policy&page=policy')
          }}
        >
          <Icon className="ion-plus-round" />
          新建告警策略
        </Button>
      </div>
      <TableSearch params={search}>
        <div className="search_item">
          <p className="search_item_label">关联资源</p>
          <Select
            value={search.Instance}
            clearable
            filterable
            onChangeValue={value => {
              setSearch({
                ...search,
                Instance: value,
                Page: 1
              })
            }}
          >
            {instances?.map(item => (
              <Option key={item.id} value={item.id}>
                {item.name}
              </Option>
            ))}
          </Select>
        </div>
        <div className="search_item">
          <p className="search_item_label">规则状态</p>
          <Select
            value={search.State}
            clearable
            onChangeValue={value => {
              setSearch({
                ...search,
                State: value,
                Page: 1
              })
            }}
          >
            <Option value={0}>全部</Option>
            <Option value={1}>启用</Option>
            <Option value={2}>停用</Option>
          </Select>
        </div>
        <div className="search_item">
          <p className="search_item_label">策略名称</p>
          <MonitorSearch
            placeholder="请输入策略，支持模糊搜索"
            value={search.PolicyName}
            onChange={value => {
              setSearch({
                ...search,
                Page: 1,
                PolicyName: value
              })
            }}
          />
        </div>
      </TableSearch>
      <Table
        data={value?.data || []}
        spreadKeys={spreadKeys}
        rowKey={data => data.PolicyID}
        checkType="none"
        animation={false}
        onChangeSpreadKeys={keys => {
          setSpreadKeys(keys)
        }}
      >
        <TableColumn key="Name" title="策略名称" />
        <TableColumn
          key="Rules"
          title="规则表达式"
          slotTemplate={([data, index]) => {
            if (data.rules?.length > 1) {
              const enable = data.rules.filter(item => item.State === 1)
              return (
                <span className="table_status">
                  {`共 ${data.rules?.length} 条规则 |`}
                  <b style={{ color: '#4BD86C' }}>{enable?.length}</b>启用
                  <b style={{ color: '#E64639' }}>{data.rules?.length - enable?.length}</b>
                  停用
                </span>
              )
            } else {
              return (
                <>
                  <p>{data.Alert || data.Name}</p>
                  <Ellipsis className="table_text" maxLines={2} hoverable>
                    {data.Expr}
                  </Ellipsis>
                </>
              )
            }
          }}
        ></TableColumn>
        <TableColumn
          key="BindInstances"
          title="关联资源"
          slotTemplate={([data]) => {
            return data.BindInstances?.length
              ? data.BindInstances?.map(item => {
                  return (
                    <>
                      <p>{item.InstanceName}</p>
                      <Ellipsis className="table_text" maxLines={2} hoverable>
                        {item.InstanceID}
                      </Ellipsis>
                    </>
                  )
                })
              : '-'
          }}
        ></TableColumn>
        <TableColumn
          key="State"
          title="启停状态"
          width="150"
          slotTemplate={([data]) => {
            const enable = data.rules?.filter(item => item.State === 1)
            return data.RuleID ? (
              <Switch
                size="small"
                value={data.State === 1 ? true : false}
                beforeChange={async from => {
                  if (from) {
                    const result = await Dialog.confirm({
                      title: '是否确认停用所选的告警规则？',
                      content: '如果停用的告警规则正在触发告警，停用之后将收到该规则的恢复通知。',
                      okText: '确认停用'
                    }).then(
                      res => {
                        return true
                      },
                      err => {
                        return false
                      }
                    )
                    return result
                  } else {
                    return true
                  }
                }}
                onChangeValue={value => {
                  updateStateFetch({ RuleID: data.RuleID, State: value })
                }}
              />
            ) : (
              <div className="ksc-space">
                {enable?.length !== data.rules?.length ? (
                  <a
                    onClick={() => {
                      updateStateFetch({ PolicyID: data.PolicyID, State: true })
                    }}
                  >
                    全部启用
                  </a>
                ) : null}
                {enable?.length ? (
                  <a
                    onClick={() => {
                      let dialog: Dialog
                      Dialog.confirm({
                        title: '是否确认停用所选的告警策略？',
                        content: (
                          <div>
                            <p>告警策略包括的告警规则将同时停用。</p>
                            <p>如果停用的告警规则正在触发告警，停用后将收到该规则的恢复通知。</p>
                          </div>
                        ),
                        ref: _dialog => (dialog = _dialog),
                        okText: '确认停用',
                        ok: async () => {
                          dialog.showLoading()

                          await updateStateFetch({ PolicyID: data.PolicyID, State: false })
                          dialog.close()
                        }
                      })
                    }}
                  >
                    全部停用
                  </a>
                ) : null}
              </div>
            )
          }}
        ></TableColumn>
        <TableColumn
          key="For"
          title="持续时间"
          width="100"
          slotTemplate={([data]) => {
            return data?.children?.length > 1 ? '-' : data.children?.[0]?.For || data.For
          }}
        ></TableColumn>
        <TableColumn
          key="Recipients"
          title="通知对象"
          width="120"
          slotTemplate={([data]) => {
            const { Recipients, WebHooks, URLNotices } = data
            return Recipients?.length || WebHooks?.length || URLNotices?.length ? (
              <div className="ksc-space">
                {Recipients?.length ? (
                  <DeleteConfirm
                    content={
                      <>
                        <TablePage
                          tableProps={{
                            checkType: 'none',
                            rowKey: data => data.ContactID,
                            scheme: reciverScheme,
                            data: Recipients.map(item => ({
                              ...item,
                              ContactChannels: handleWayToChannels(
                                item.ContactChannels || item.ContactWay
                              )
                            }))
                          }}
                        ></TablePage>
                      </>
                    }
                  >
                    <a>联系人</a>
                  </DeleteConfirm>
                ) : null}
                {WebHooks?.length || URLNotices?.length ? (
                  <DeleteConfirm
                    content={
                      <TablePage
                        tableProps={{
                          checkType: 'none',
                          rowKey: data => data.ContactID,
                          scheme: urlNoticeScheme,
                          data: URLNotices || handleCallbackType(WebHooks)
                        }}
                      ></TablePage>
                    }
                  >
                    <a>回调</a>
                  </DeleteConfirm>
                ) : null}
              </div>
            ) : (
              '-'
            )
          }}
        ></TableColumn>
        <TableColumn
          key="operate"
          title="操作"
          width="100"
          slotTemplate={([data]) => {
            const findPolicy = value.data.find(item => {
              return item.children?.find(rule => rule.RuleID === data.RuleID)
            })
            return (
              <div className="ksc-space">
                {/* 编辑 */}
                <a
                  onClick={() => {
                    let url = `/monitor/prometheusmonitor/alarm/edit?tab=policy`
                    if (data.PolicyID) {
                      url += `&policyid=${data.PolicyID}`
                    }
                    if (data.RuleID) {
                      url += `&ruleid=${data.RuleID}`
                    }
                    if (data.PolicyID) {
                      history.push(url)
                    } else {
                      Message.error('策略ID为空')
                    }
                  }}
                >
                  编辑
                </a>
                {/* 删除 */}
                <a
                  onClick={() => {
                    let dialog: Dialog
                    data.RuleID && !data.isRule
                      ? Dialog.confirm({
                          title: '是否确认删除所选的告警规则？',
                          content: (
                            <div>
                              <p>
                                如果删除的告警规则正在触发告警，删除之后将收到该规则的恢复通知。
                              </p>
                              {findPolicy?.children?.length === 1 ? (
                                <p style={{ color: '#E64639' }}>
                                  告警策略仅包括一条规则，将同步删除告警策略信息。
                                </p>
                              ) : null}
                            </div>
                          ),
                          ref: _dialog => (dialog = _dialog),
                          okText: '确认删除',
                          ok: async () => {
                            dialog.showLoading()
                            await deleteFetch({ RuleID: data.RuleID })
                            dialog.close()
                          }
                        })
                      : Dialog.confirm({
                          title: '是否确认该告警策略？',
                          content: (
                            <div>
                              <p>告警策略包括的告警规则将同时删除。</p>
                              <p>
                                如果删除的告警规则正在触发告警，删除之后将收到该规则的恢复通知。
                              </p>
                            </div>
                          ),
                          ref: _dialog => (dialog = _dialog),
                          okText: '确认删除',
                          ok: async () => {
                            dialog.showLoading()
                            await deleteFetch({ PolicyID: data.PolicyID })
                            dialog.close()
                          }
                        })
                  }}
                >
                  删除
                </a>
              </div>
            )
          }}
        ></TableColumn>
      </Table>
      <Pagination
        value={search.Page}
        limit={search.PageSize}
        total={value?.total || 0}
        size="small"
        onChangeValue={value => {
          setSearch(state => {
            if (state.Page !== value) {
              setSpreadKeys([])
            }
            return {
              ...search,
              Page: value
            }
          })
        }}
        onChangeLimit={limit => {
          setSearch({
            ...search,
            PageSize: limit
          })
        }}
      />
    </div>
  )
}

export default styled(PrometheusAlarmPolicy)`
  .k-table {
    .table_status {
      b {
        margin: 0 4px;
      }
    }
  }
`
